import { createCanvas } from '@/utils/file-kit';
import { getMainLayerRenderSize } from '@/utils/layer-kit';
import { IImageLayerProps, ILayerProps, ISize } from '@/version-types';
import { getDrawPenSVG } from '../components/draw-pen-path';
import { EliminateConfig, IPathInfo } from '../types';

/**
 * 通过 SVG 合成蒙版
 *
 * @param svg - SVG 字符串
 * @param size - 蒙版尺寸
 * @returns 合成后的 Canvas 对象
 */
export function createMaskFromSVG(svg: string, size: ISize, backgroundColor?: string): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
        const { canvas, ctx } = createCanvas(size.width, size.height);
        const svgURL = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
        let svgElement: HTMLImageElement | null = document.createElement('img');

        svgElement.src = svgURL;
        svgElement.onload = () => {
            if (backgroundColor) {
                ctx.fillStyle = backgroundColor;
                ctx.fillRect(0, 0, size.width, size.height);
            }
            ctx.drawImage(svgElement!, 0, 0, size.width, size.height);
            URL.revokeObjectURL(svgURL);
            svgElement = null;
            resolve(canvas);
        };

        svgElement.onerror = () => {
            reject(new Error('Failed to load SVG'));
        };
    });
}

export async function getMaskCanvas(
    pathInfos: IPathInfo[],
    originLayer: ILayerProps | null,
    eliminateConfig: EliminateConfig,
    fillColor?: string,
    backgroundColor?: string,
) {
    const {
        type,
        watermarkMaskURL,
        textMaskURL,
        autoRecognizeWatermark,
        autoRecognizeText,
        isWatermarkMaskUsed,
        isTextMaskUsed,
    } = eliminateConfig;

    // 消除都是拿原图去消除，所以创建的蒙版也是要按照原图的尺寸
    if (!originLayer) return null;
    const originData = originLayer.data as IImageLayerProps;
    const size = getMainLayerRenderSize(originLayer);

    const svg = getDrawPenSVG(
        size,
        pathInfos,
        type,
        autoRecognizeWatermark && !isWatermarkMaskUsed ? watermarkMaskURL : '',
        autoRecognizeText && !isTextMaskUsed ? textMaskURL : '',
        fillColor,
    );

    const svgMaskCanvas = await createMaskFromSVG(svg, size, backgroundColor);

    if (!svgMaskCanvas) return null;

    const { canvas: eliminateMaskCanvas, ctx: eliminateMaskCtx } = createCanvas(
        originData?.naturalWidth || 0,
        originData?.naturalHeight || 0,
    );

    eliminateMaskCtx?.drawImage(
        svgMaskCanvas,
        0,
        0,
        size.width,
        size.height,
        0,
        0,
        eliminateMaskCanvas.width,
        eliminateMaskCanvas.height,
    );

    return eliminateMaskCanvas;
}
